import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';
import { CertificationOverview, CertSection } from '../core/models';

/** VAR-agent certification — section hub (AB#1827 follow-on, per-section quiz
 * flow). Replaced the old module-list + "Start exam" page (the retired
 * `TrainComponent`/`/certification/train` route).
 *
 * Reads `GET /me/certification` (via `getCertificationOverview()`) exactly
 * once on init — the taker-self endpoint, resolved to the `sections[]` shape.
 * Each section card links to `/certification/section/:domain` — `domain` is
 * the canonical id the section page and quiz API key on (`SupportApiService`'s
 * `getSectionModule`/`startSectionQuiz`/`submitSectionQuiz` all take that same
 * slug as `section`), NOT `code`, which is a display/audit label only. */
@Component({
  selector: 'app-certification-hub',
  standalone: true,
  imports: [CommonModule, RouterLink, NgbTooltip],
  template: `
    <div class="bt-page">
      <div class="bt-page__head">
        <h1 class="bt-page__title">VAR Certification</h1>
        <p class="bt-page__subtitle">
          Read each section, then pass its 5-question quiz. Certification unlocks the queue inbox.
        </p>
      </div>

      @if (loading) {
        <div class="bt-empty"><span class="bt-spinner"></span><span class="bt-empty__hint">Loading your certification status…</span></div>
      } @else if (error) {
        <div class="bt-empty" data-testid="cert-hub-error">
          <span class="bt-empty__icon">⚠️</span>
          <span class="bt-empty__title">Couldn't load your certification status</span>
          <span class="bt-empty__hint">{{ error }}</span>
          <button type="button" class="bt-btn bt-btn--secondary bt-btn--sm" data-testid="cert-hub-retry" (click)="load()">
            Try again
          </button>
        </div>
      } @else if (overview) {
        <div class="bt-panel progress-panel" data-testid="cert-progress"
             ngbTooltip="Every section must be passed at least once to certify">
          <div class="progress-panel__row">
            <span class="progress-panel__text">
              <strong>{{ overview.passed_count }}</strong> of {{ overview.total }} sections passed
            </span>
            @if (overview.certified) {
              <span class="bt-badge bt-badge--success" data-testid="cert-certified-badge">✓ Certified</span>
            }
          </div>
          <div class="progress-track" role="img" [attr.aria-label]="overview.passed_count + ' of ' + overview.total + ' sections passed'">
            <div class="progress-track__fill" [style.width.%]="progressPercent()"></div>
          </div>
        </div>

        @if (overview.certified) {
          <div class="bt-panel certified-panel" data-testid="cert-certified-panel">
            <span class="certified-panel__icon">🎉</span>
            <div class="certified-panel__body">
              <h2 class="certified-panel__title">You're certified</h2>
              <p class="certified-panel__hint">
                You can now work tickets from the queue inbox. Review a section any time to brush up.
              </p>
            </div>
            <a class="bt-btn bt-btn--primary" routerLink="/inbox" data-testid="cert-go-inbox"
               ngbTooltip="You're certified — open the queue inbox">
              Go to Inbox
            </a>
          </div>
        }

        <div class="section-grid">
          @for (s of overview.sections; track s.domain) {
            <a class="bt-panel section-card" [routerLink]="['/certification/section', s.domain]"
               [attr.data-testid]="'section-card-' + s.domain"
               [ngbTooltip]="s.passed ? 'Review this section and retake its quiz' : 'Read this section and take its quiz'">
              <div class="section-card__head">
                <h2 class="section-card__title">{{ s.title }}</h2>
                <span class="bt-badge" [class]="s.passed ? 'bt-badge--success' : 'bt-badge--neutral'"
                      [attr.data-testid]="'section-status-' + s.domain">
                  {{ s.passed ? 'Passed ✓' : 'Not started' }}
                </span>
              </div>
              <p class="section-card__summary">{{ s.summary }}</p>
              <span class="section-card__action">{{ s.passed ? 'Review' : 'Start' }} →</span>
            </a>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    // ---------------------------------------------------------------- progress header
    .progress-panel { padding: 1rem 1.25rem; margin-bottom: 1.25rem; }
    .progress-panel__row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.6rem; }
    .progress-panel__text { font-size: 15px; color: var(--bt-text-primary); }
    .progress-panel__text strong { font-family: 'Montserrat', sans-serif; font-size: 17px; }
    .progress-track {
      height: 6px; border-radius: 999px; background: var(--bt-bg-surface-2); overflow: hidden;
    }
    .progress-track__fill {
      height: 100%; border-radius: 999px; background: var(--bt-status-success);
      transition: width .3s ease;
    }

    // ---------------------------------------------------------------- certified celebration
    .certified-panel {
      display: flex; align-items: center; gap: 1rem; padding: 1.1rem 1.25rem; margin-bottom: 1.25rem;
      border-color: color-mix(in srgb, var(--bt-status-success) 35%, var(--bt-border-light));
      background: color-mix(in srgb, var(--bt-status-success) 6%, var(--bt-bg-surface));
    }
    .certified-panel__icon { font-size: 1.75rem; line-height: 1; }
    .certified-panel__body { flex: 1; }
    .certified-panel__title { margin: 0; font-family: 'Montserrat', sans-serif; font-size: 16px; font-weight: 700; color: var(--bt-text-primary); }
    .certified-panel__hint { margin: 0.2rem 0 0; font-size: 13px; color: var(--bt-text-secondary); }

    // ---------------------------------------------------------------- section grid
    .section-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 1rem;
    }
    .section-card {
      display: flex; flex-direction: column; padding: 1rem 1.15rem; text-decoration: none;
      color: inherit; cursor: pointer;
      transition: box-shadow .15s ease, transform .1s ease, border-color .15s ease;
    }
    .section-card:hover {
      box-shadow: var(--bt-shadow-md, 0 4px 16px rgba(0,0,0,.12)); transform: translateY(-2px);
      border-color: var(--bt-border-focus);
    }
    .section-card:focus-visible { outline: 2px solid var(--bt-border-focus); outline-offset: 2px; }
    .section-card__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.6rem; margin-bottom: 0.4rem; }
    .section-card__title { margin: 0; font-family: 'Montserrat', sans-serif; font-size: 15px; font-weight: 700; color: var(--bt-text-primary); }
    .section-card__summary { flex: 1; margin: 0 0 0.75rem; font-size: 13px; line-height: 1.5; color: var(--bt-text-secondary); }
    .section-card__action { align-self: flex-end; font-size: 13px; font-weight: 600; color: var(--bt-brand-intercept); }
  `],
})
export class CertificationHubComponent implements OnInit {
  private api = inject(SupportApiService);

  overview: CertificationOverview | null = null;
  loading = true;
  error: string | null = null;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.api.getCertificationOverview().subscribe({
      next: (overview) => { this.overview = overview; this.loading = false; },
      error: () => { this.error = 'Could not load your certification status. Try again.'; this.loading = false; },
    });
  }

  progressPercent(): number {
    if (!this.overview || this.overview.total <= 0) return 0;
    return Math.min(100, Math.round((this.overview.passed_count / this.overview.total) * 100));
  }
}
